/**
 * utilities/directories.ts
 * desc: helpers for the source-directories payload
 * note: extracted from store/reducers/sources.js so per-instance consumers
 *       (DirectoryInput) can flatten raw getDirectories results without reading the
 *       shared directoryList slice
 */

export interface Directory {
    id?: string;
    name?: string;
    childDirectories?: Directory[];
    parentId?: string | null;
    [key: string]: unknown;
}

/**
 * formatDirectoriesPayload
 * desc: Change the format of initial directories payload
 * note: returns a flattened payload — every nested folder follows its root,
 *       tagged with the root's id as parentId
 * @param  {array}  payload  Orginal array
 * @return {array}           New flattened array
 */
export function formatDirectoriesPayload(payload: Directory[] = []): Directory[] {
    return payload.reduce<Directory[]>((flattenedDirectories, currentDirectory) => {
        const mergedDirectories = _mergeChildDirectories(currentDirectory.childDirectories, currentDirectory.id);
        const currentItem = mergedDirectories.length > 0 ? { ...currentDirectory, childDirectories: mergedDirectories } : currentDirectory;

        return [ ...flattenedDirectories, currentItem, ...mergedDirectories ];
    }, []);
}

/**
 * _mergeChildDirectories
 * desc: merges nested subfolders into single array
 * note: this is a recursive function
 * @param  {array}  directoriesList  Original array
 * @return {array}                   New array w all nested folders
 */
function _mergeChildDirectories(directoriesList: Directory[] | undefined, parentId: string | null = null): Directory[] {
    const result: Directory[] = [];

    if (directoriesList && directoriesList.length > 0) {
        directoriesList.forEach((directory) => {
            result.push({ ...directory, parentId });
            if (directory.childDirectories && directory.childDirectories.length > 0) {
                const subfolders = [ ..._mergeChildDirectories(directory.childDirectories, parentId) ];
                // If a nested directory has the same name as a higher directory in the same optgroup, then prefix the nested directory with the name of the parent
                result.push(...subfolders.map(item => ({ ...item, parentId, name: item.name === directory.name ? `${directory.name} ${item.name}` : item.name })));
            }
        });
    }

    return result;
}
